import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18next';
import { css } from '@emotion/core';

import useClientHistory, { PRIZE, JOIN, SIGNUP_FORM, LOTTERY } from 'client/hooks/useClientHistory';
import useGameActionEnableInfo from 'client/hooks/useGameActionEnableInfo';
import useUpdateWinnerData from 'client/hooks/useUpdateWinnerData';
import useSystemBtnStyleByTheme from 'client/hooks/useSystemBtnStyleByTheme';

import ViewPrizeListButton from 'client/components/Result/ViewPrizeListButton';
import PlayNewEntryButton from 'client/components/Result/Actions/PlayNewEntryButton';
import PlayAgainWithoutNewEntryButton from 'client/components/Result/Actions/PlayAgainWithoutNewEntryButton';
import SignUpFormButton from 'client/components/Result/Actions/SignUpFormButton';
import OpenWinnerButton from 'client/components/Result/Actions/OpenWinnerButton';
import ActionHeader from 'client/components/Result/Actions/ActionHeader';
import { scrollPromoBodyIntoView } from 'client/utils/scroll';
import { removeKeysFromQueryString } from 'client/utils/queryString';
import getWinnerLabelAndBtnTextGenerator from 'client/components/Result/utils/getWinnerLabelAndBtnTextGenerator';
import { resultHeaderContainerStyle } from 'client/styles/common';
import { loginTypeMappingPointRoutesParam } from 'config/loginTypes';
import {
    isDoNotShowBackToJoinPageBtnGames,
    isDoNotShowPlayNewEntryBtnGames,
    isCanShowOpenWinnerBtnGame,
    isCanShowSignUpFormBtnGame
} from 'config/promoGameTypesClientConfig';
import { setReJoin } from 'client/redux/modules/flags';
import useGetNotifyData from 'client/hooks/useGetNotifyData';
import {
    NOTIFY_STATUS_TYPE_MAP,
    NOTIFY_DELIVER_TYPE_MAP,
    NOTIFY_SOURCE_TYPE_MAP
} from 'config/notifyTypes';
import useResultPage from 'hooks/useResultPage';

function ResultHeader({ themeStyle }) {
    const dispatch = useDispatch();
    const history = useClientHistory();
    const { isFetching: isFetchingWinner } = useUpdateWinnerData();

    const userGameResultRecords = useSelector(s => s.userGameResultRecords);
    const { primaryBtnStyle, viewPrizeListBtnStyle } = useSystemBtnStyleByTheme(themeStyle);
    const { hint: signUpHint } = useSelector(s => s.signUpForm);
    const actionEnableState = useGameActionEnableInfo();
    const winnerRef = useRef();
    const openWinnerButtonRef = useRef();
    const { type: gameType, uuid: promoUUID } = useSelector(s => s.promo);
    const { loginMethod } = useSelector(s => s.settingPromo);
    const { isMgmShare, isMgmSelf } = useSelector(s => s.flags);
    const { verifyMechanism } = useSelector(s => s.settingPromo);
    const resultPage = useResultPage();
    const { rejoinButtonText, winnerButtonText } = resultPage;
    // TODO :  ▽ 待後端更新新版審核機制設定後移除區塊內程式碼 ---------
    const { enableMediaValidation, verifyMechanism: oldVerifyMechanism } = useSelector(
        s => s.settingGame
    );
    const isVerifyGame =
        enableMediaValidation === 0 || verifyMechanism === 0 || oldVerifyMechanism?.type === 0;
    // TODO : △ 待後端更新新版審核機制設定後移除區塊內程式碼 ----------

    // TODO : ▽ 待後端更新新版審核機制設定後解除註解 ---------

    // [860rze7wg] 把顯示剩餘抽獎/領獎次數文字的顯示從 OpenWinnerButton 抽到外層
    const [actionHeader, setActionHeader] = useState(null);
    // [860rzpygd] [#860ru1tem] 當有獎項未開獎時，優先開獎後再顯示再次參加按鈕
    const [unOpenPrizeQuantity, setUnOpenPrizeQuantity] = useState(0);
    // [#860rzwuv7] openWinnerButtonRef 在 open winner button 雖然有更新 winner 與 un open winner list 讓領獎按鈕做顯示或是隱藏
    // 但父層元件只會取得 openWinnerButtonRef 第一次 render 的值，後續操作造成的 re-render 都不會更新 ref value，所以需要透過 state + callback 去 trigger useEffect update
    const [openWinnerChangedFlag, setOpenWinnerChangedFlag] = useState(false);

    const hasGameResultRecords = userGameResultRecords.some(record =>
        Boolean(record.flow?.gameResult)
    );

    // 可以開啟通知的條件:
    // 條件1. 要有開登入
    // 條件2. 活動是邀請模組 or 活動開啟分享獎 or 活動開啟審核功能(且沒開預設通過)
    const isLoginPromo = Object.keys(loginTypeMappingPointRoutesParam)
        .map(Number)
        .includes(loginMethod);
    const isMGMPromo = isMgmShare || isMgmSelf;
    const enableNotifyPromo = isMGMPromo || isVerifyGame;
    const shouldShowSignUpFormButton =
        !isFetchingWinner &&
        isCanShowSignUpFormBtnGame(gameType) &&
        actionEnableState.canFillSignupform;
    const { data, isLoading: isLoadingNotify, postNotification } = useGetNotifyData({
        promoUUID,
        dependency: [isLoginPromo]
    });

    function handleToPrize({ prizeUUID, entriesUUID }) {
        history.push(PRIZE, { prizeUUID, entriesUUID });
    }

    function handleToJoin() {
        dispatch(setReJoin(true));
        const shouldRemoveQsKey = ['pcode'];
        const qs = removeKeysFromQueryString(shouldRemoveQsKey);
        const location = {
            ...window.location,
            search: `?${qs}`
        };
        history.replace(JOIN, '', location);
    }

    function handleToSignupForm() {
        history.push(SIGNUP_FORM);
    }
    function handleOpenWinner({ entriesUUID, prizeUUID, animatingLottery }) {
        scrollPromoBodyIntoView();
        history.push(LOTTERY, {
            animatingLottery,
            entriesUUID,
            prizeUUID
        });
    }
    // 可以顯示 再次參加 (會 create entry) 按鈕
    const canCreateNewEntryAndJoinAgain = Boolean(
        !isDoNotShowPlayNewEntryBtnGames(gameType) &&
            actionEnableState.canGetNewEntry &&
            !isFetchingWinner &&
            unOpenPrizeQuantity === 0
    );
    // 可以顯示 再次參加(不會 create entry) 按鈕
    const canJoinAgainWithoutCreateNewEntry = Boolean(
        !isDoNotShowBackToJoinPageBtnGames(gameType) &&
            actionEnableState.canBackToJoinGamePage &&
            !isFetchingWinner &&
            unOpenPrizeQuantity === 0
    );

    // 可以顯示 領取獎品 按鈕 (抽獎 => 試手氣 / 參加獎 => 領取獎品 / 好禮自由選 => 選擇獎品)
    const canShowOpenWinnerBtnGame = Boolean(
        isCanShowOpenWinnerBtnGame(gameType) && !isFetchingWinner
    );

    useEffect(() => {
        if (openWinnerButtonRef?.current) {
            let title = '';
            const { getOpenWinnerButtonData } = openWinnerButtonRef.current;
            const {
                shouldShowButton,
                hasEnableLotteryParticipant,
                hasPendingVerifyEntry,
                openWinnerList,
                pickWinnerList
            } = getOpenWinnerButtonData();
            const quantity = openWinnerList.length || pickWinnerList.length;
            const { header } = getWinnerLabelAndBtnTextGenerator({
                lotteryType: openWinnerList[0]?.lotteryType || pickWinnerList[0]?.lotteryType,
                customBtnText: winnerButtonText
            });
            if (!shouldShowButton && hasEnableLotteryParticipant && hasPendingVerifyEntry) {
                title = i18n.t('label.prize_after_verify_can_has_prize');
            } else {
                title = quantity > 0 ? header(quantity) : '';
            }
            const comp = (
                <ActionHeader
                    title={title}
                    titleStyle={`color: ${themeStyle.titleColor};`}
                    buttonStyle={`margin: 0`}
                />
            );
            setUnOpenPrizeQuantity(quantity);
            setActionHeader(comp);
        }
    }, [themeStyle.titleColor, openWinnerChangedFlag, winnerButtonText]);

    useEffect(() => {
        // 可以執行通知的條件
        if (
            enableNotifyPromo &&
            Boolean(isLoginPromo) &&
            hasGameResultRecords &&
            !isLoadingNotify &&
            data.length > 0
        ) {
            initNotify();
        }

        async function initNotify() {
            // 第 0 筆是 Email，第一筆是 Line Notify，不過 line Notify 已經棄用，所以可以不顯示 UI 給 User 選擇，直接開啟 Email 通知即可
            const notifySettings = data[0];
            const { list, description } = notifySettings;
            const targetNotifyItem = list.find(({ disabled }) => !disabled);
            if (
                targetNotifyItem.value &&
                targetNotifyItem.source &&
                targetNotifyItem.type &&
                targetNotifyItem.status === NOTIFY_STATUS_TYPE_MAP.INACTIVE
            ) {
                postNotification({
                    type: targetNotifyItem.type,
                    source: NOTIFY_SOURCE_TYPE_MAP.MANUAL_INPUT,
                    value:
                        targetNotifyItem.type === NOTIFY_DELIVER_TYPE_MAP.EMAIL
                            ? description
                            : undefined
                });
            }
        }
    }, [
        data,
        enableNotifyPromo,
        hasGameResultRecords,
        isLoadingNotify,
        isLoginPromo,
        postNotification
    ]);

    return (
        <div css={css(resultHeaderContainerStyle)}>
            {shouldShowSignUpFormButton ? (
                <>
                    <ActionHeader
                        title={signUpHint}
                        titleStyle={`color: ${themeStyle.titleColor};`}
                        buttonStyle={`margin: 0`}
                    />
                    <div css={css(flexCenterStyle)}>
                        <SignUpFormButton
                            themeStyle={themeStyle}
                            btnStyle={primaryBtnStyle}
                            onClick={handleToSignupForm}
                        />
                        <ViewPrizeListButton
                            btnStyle={viewPrizeListBtnStyle}
                            onToPrize={handleToPrize}
                            isLoading={isFetchingWinner}
                            ref={winnerRef}
                        />
                    </div>
                </>
            ) : (
                <>
                    {actionHeader}
                    <div css={css(flexCenterStyle)}>
                        {/* TODO: 領取獎品按鈕的顯示有別於其他按鈕，不在這隻檔案做控制，此按鈕元件內會自己控制顯示邏輯 */}
                        {canShowOpenWinnerBtnGame && (
                            <OpenWinnerButton
                                onOpenWinner={handleOpenWinner}
                                openWinnerListChangeCallback={isChanged =>
                                    setOpenWinnerChangedFlag(isChanged)
                                }
                                enableGetWinnerByApi={actionEnableState.isQualifiedToGetWinner}
                                btnStyle={primaryBtnStyle}
                                ref={openWinnerButtonRef}
                            />
                        )}
                        {canCreateNewEntryAndJoinAgain && (
                            <div css={css(btnContainerStyle)}>
                                <PlayNewEntryButton
                                    btnStyle={primaryBtnStyle}
                                    onClick={handleToJoin}
                                    rejoinButtonText={rejoinButtonText}
                                />
                            </div>
                        )}
                        {canJoinAgainWithoutCreateNewEntry && (
                            <div css={css(btnContainerStyle)}>
                                <PlayAgainWithoutNewEntryButton
                                    btnStyle={primaryBtnStyle}
                                    onClick={handleToJoin}
                                    rejoinButtonText={rejoinButtonText}
                                />
                            </div>
                        )}

                        <ViewPrizeListButton
                            btnStyle={viewPrizeListBtnStyle}
                            onToPrize={handleToPrize}
                            isLoading={isFetchingWinner}
                            ref={winnerRef}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default React.memo(ResultHeader);

const btnContainerStyle = css`
    width: 100%;
`;

const flexCenterStyle = css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
`;
